import { setUser as sentrySetUser } from "@sentry/browser";
import { bypassCognito } from "common/utils/context";
import * as cognito from "./cognito";
import * as cookie from "./cookie";

// Dedupes concurrent getToken() calls so only one Cognito refresh runs at a
// time; concurrent refresh-token grants can race and produce transient 401s.
let inflight: Promise<string | null> | null = null;

// Kept separate from `inflight`: a forced refresh must never resolve to a cached token.
let forcedInflight: Promise<string | null> | null = null;

export interface GetTokenOptions {
  /** Bypass cached tokens and force a refresh; used after a 401. */
  force?: boolean;
}

export async function getToken(options: GetTokenOptions = {}): Promise<string | null> {
  if (bypassCognito()) {
    if (options.force) {
      // Nothing to refresh in offline/dev mode; null aborts the 401 retry.
      return null;
    }

    const userCookie = cookie.get(cognito.HIGHARC_USER_COOKIE);
    if (!userCookie) {
      return null;
    }

    const { jwtToken } = JSON.parse(userCookie);
    return jwtToken;
  }

  if (options.force) {
    // Wait for any in-flight normal refresh to settle first: two concurrent
    // grants share one refresh token, and a rejected grant logs the user out.
    // Its result is discarded — it may be the token just rejected.
    if (inflight) {
      await inflight.catch(() => null);
    }

    // Capture the promise locally: `finally` runs per awaiter, so a late
    // awaiter must not clear a newer forcedInflight.
    const pending = forcedInflight ?? (forcedInflight = resolveToken(true));
    try {
      return await pending;
    } finally {
      if (forcedInflight === pending) {
        forcedInflight = null;
      }
    }
  }

  // A forced refresh's result is at least as fresh as anything this call could
  // get on its own, and racing it risks invalidating the concurrent grant.
  if (forcedInflight) {
    return forcedInflight;
  }

  if (inflight) {
    return inflight;
  }

  inflight = resolveToken(false);
  try {
    return await inflight;
  } finally {
    inflight = null;
  }
}

async function resolveToken(force: boolean): Promise<string | null> {
  // Password-auth users via the Cognito SDK. When forced, the cached session
  // may hold the token the server just rejected (getSession only refreshes
  // after local expiry), so go straight to a refresh-token grant instead.
  const user = cognito.getUser();
  if (user) {
    try {
      const username = user.getUsername();
      sentrySetUser({ email: username });

      const session = force ? await cognito.refreshSession(user) : await cognito.getSession(user);

      const jwtToken = session.getIdToken().getJwtToken();
      cookie.set(cognito.HIGHARC_USER_COOKIE, JSON.stringify({ username, jwtToken }));
      return jwtToken;
    } catch (e) {
      console.error("Unable to establish cognito session for user");
    }
  }

  // SSO users authenticate via Hosted UI — no SDK session in this tab. Fall
  // back to the shared auth cookie (works across tabs) before refreshing;
  // forced refreshes skip it since the server just rejected that token.
  if (!force) {
    const cookieToken = cognito.getValidJwtFromUserCookie();
    if (cookieToken) {
      const email = cognito.getEmailFromJwt(cookieToken);
      if (email) {
        sentrySetUser({ email });
      }
      return cookieToken;
    }
  }

  // Fall back to SSO refresh token (federated / Google SSO users)
  const ssoToken = await cognito.refreshSSOToken();
  if (ssoToken) {
    const email = cognito.getEmailFromJwt(ssoToken) ?? "";
    sentrySetUser({ email });
    cookie.set(cognito.HIGHARC_USER_COOKIE, JSON.stringify({ cognitoName: email, jwtToken: ssoToken }));
    return ssoToken;
  }

  sentrySetUser(null);
  return null;
}
